import { ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "./utils";

export interface SitePaginationProps {
  /** Current page (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items across all pages */
  totalCount: number;
  /** Number of items per page (used for "Showing X–Y of Z") */
  pageSize: number;
  /** Called when user selects a page */
  onPageChange: (page: number) => void;
  /** Label for the count text, e.g. "vendors", "items" */
  itemLabel?: string;
  /** Show "Showing X–Y of Z items" row. Default true */
  showCount?: boolean;
  /** Optional class for the root wrapper */
  className?: string;
  /** Primary color (border, bg when active). Default site red */
  primaryColor?: string;
}

const DEFAULT_PRIMARY = "#7A1E1E";

type PageItem = number | "ellipsis-left" | "ellipsis-right";

function getPageNumbers(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: PageItem[] = [1];
  const showLeft = currentPage > 2;
  const showRight = currentPage < totalPages - 1;
  if (showLeft) {
    pages.push("ellipsis-left");
  }
  const start = showLeft ? Math.max(2, currentPage - 1) : 2;
  const end = showRight
    ? Math.min(totalPages - 1, currentPage + 1)
    : totalPages - 1;
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (showRight) {
    pages.push("ellipsis-right");
  }
  if (totalPages > 1) {
    pages.push(totalPages);
  }
  return pages;
}

export function SitePagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  itemLabel = "items",
  showCount = true,
  className,
  primaryColor = DEFAULT_PRIMARY,
}: SitePaginationProps) {
  if (totalPages < 2 && !showCount) {
    return null;
  }

  const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);
  const pages = getPageNumbers(currentPage, totalPages);

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const prevNextBase =
    "rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:cursor-not-allowed inline-flex min-w-[6rem] items-center gap-2";
  const prevNextEnabled =
    "cursor-pointer bg-white hover:border-[#8B2E2E] hover:bg-[#8B2E2E]/10";
  const prevNextDisabled = "border-gray-200 bg-gray-50 text-gray-400";
  const pageBtnBase =
    "flex h-9 min-w-[2.25rem] cursor-pointer items-center justify-center rounded-lg border-2 px-3 text-sm font-medium transition-colors";

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {showCount && (
        <p className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold" style={{ color: primaryColor }}>
            {totalCount === 0 ? "0" : `${start}–${end}`}
          </span>{" "}
          of{" "}
          <span className="font-semibold" style={{ color: primaryColor }}>
            {totalCount}
          </span>{" "}
          {itemLabel}
        </p>
      )}

      {totalPages >= 2 && (
        <div className="flex w-full items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={isFirstPage}
            aria-label="Previous page"
            aria-disabled={isFirstPage}
            className={cn(
              prevNextBase,
              "justify-start",
              isFirstPage ? prevNextDisabled : prevNextEnabled
            )}
            style={
              isFirstPage
                ? undefined
                : { borderColor: `${primaryColor}40`, color: primaryColor }
            }
          >
            <ChevronRight className="h-4 w-4 shrink-0 rotate-180" />
            Previous
          </button>

          <nav aria-label="Pagination" className="flex flex-1 justify-center">
            <ul className="flex items-center gap-1">
              {pages.map(page =>
                page === "ellipsis-left" || page === "ellipsis-right" ? (
                  <li
                    key={page}
                    className="flex items-center justify-center px-1"
                  >
                    <MoreHorizontal
                      className="h-4 w-4"
                      style={{ color: `${primaryColor}80` }}
                    />
                  </li>
                ) : (
                  <li key={page}>
                    <button
                      type="button"
                      onClick={() => onPageChange(page)}
                      aria-current={currentPage === page ? "page" : undefined}
                      aria-label={`Page ${page}`}
                      className={cn(
                        pageBtnBase,
                        currentPage === page
                          ? "border-white text-white"
                          : "hover:border-[#7A1E1E] hover:bg-[#7A1E1E]/10"
                      )}
                      style={
                        currentPage === page
                          ? {
                              backgroundColor: primaryColor,
                              borderColor: primaryColor,
                            }
                          : {
                              borderColor: `${primaryColor}40`,
                              color: primaryColor,
                            }
                      }
                    >
                      {page}
                    </button>
                  </li>
                )
              )}
            </ul>
          </nav>

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={isLastPage}
            aria-label="Next page"
            aria-disabled={isLastPage}
            className={cn(
              prevNextBase,
              "justify-end",
              isLastPage ? prevNextDisabled : prevNextEnabled
            )}
            style={
              isLastPage
                ? undefined
                : { borderColor: `${primaryColor}40`, color: primaryColor }
            }
          >
            Next
            <ChevronRight className="h-4 w-4 shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
}
